import { ChangeDetectionStrategy, Component, DestroyRef } from '@angular/core';
import { AlertController, IonButton, IonButtons, IonHeader, IonIcon, IonTitle, IonToolbar, ModalController } from '@ionic/angular/standalone';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { enterLeaveFast } from 'src/app/animations/common.animations';
import { ItemCheckboxComponent } from 'src/app/components/item-checkbox/item-checkbox.component';
import { ItemLabelComponent } from 'src/app/components/item-label/item-label.component';
import { ItemNoteComponent } from 'src/app/components/item-note/item-note.component';
import { ItemComponent } from 'src/app/components/item/item.component';
import { SpinnerComponent } from 'src/app/components/spinner/spinner.component';
import { SvgIconComponent } from 'src/app/components/svg-icon/svg-icon.component';
import { IsoDateToRelativePipe, TimeDisplay } from 'src/app/pipes/iso-date-to-relative.pipe';
import { AppState } from 'src/app/store/reducers';
import * as sheetTemplateManagementActions from 'src/app/store/sheet-template-management/sheet-template-management.actions';
import { sheetTemplates } from 'src/app/store/sheet-template-management/sheet-template-management.selectors';
import { SheetTemplate } from 'src/app/types/sheet.types';

@Component({
    templateUrl: './manage-sheet-templates.modal.html',
    styleUrl: './manage-sheet-templates.modal.scss',
    imports: [
        LetDirective, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonIcon, ItemComponent, ItemLabelComponent, ItemNoteComponent, IsoDateToRelativePipe,
        ItemCheckboxComponent, SvgIconComponent, SpinnerComponent
    ],
    animations: [enterLeaveFast],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ManageSheetTemplatesModal {

	TimeDisplay = TimeDisplay;

	sheetTemplates$: Observable<SheetTemplate[]>;

	constructor(private destroyRef: DestroyRef, private store: Store<AppState>, private modalController: ModalController, private alertController: AlertController) {

		this.store.dispatch(sheetTemplateManagementActions.loadTemplatesRequested());
		this.sheetTemplates$ = this.store.select(sheetTemplates);

		this.destroyRef.onDestroy(() => {
			this.store.dispatch(sheetTemplateManagementActions.templatesUnloaded());
		});
	}

	close() {
		return this.modalController.dismiss();
	}

	async presentRenameAlert(template: SheetTemplate) {
		const alert = await this.alertController.create({
			header: 'Rename template...',
			message: `'${template.name}'`,
			inputs: [
				{
					name: 'name',
					type: 'text',
					placeholder: 'name...'
				}
			],
			buttons: [
				'Cancel',
				{
					text: 'Rename',
					handler: (e: any) => {
						this.store.dispatch(sheetTemplateManagementActions.renameTemplateRequested({ templateId: template.id, newName: e.name.trim() }));
					}
				}
			],
			backdropDismiss: false
		});
		alert.present();
	}

	async presentActivationAlert(template: SheetTemplate, event: MouseEvent) {
		event.stopPropagation();
		const alert = await this.alertController.create({
			header: `${template.isActive ? 'Deactivate' : 'Activate'} template...`,
			message: `'${template.name}'`,
			buttons: [
				'Cancel',
				{
					text: template.isActive ? 'Deactivate' : 'Activate',
					handler: () => {
						this.store.dispatch(sheetTemplateManagementActions.changeTemplateActiveStateRequested({ templateId: template.id, isActive: !template.isActive }));
					}
				}
			],
			backdropDismiss: false
		});
		alert.present();
	}

	async presentDeleteAlert(template: SheetTemplate, event: MouseEvent) {
		event.stopPropagation();
		const alert = await this.alertController.create({
			header: 'Delete template...',
			message: `Are you sure you want to delete '${template.name}'? This action cannot be undone`,
			buttons: [
				'Cancel',
				{
					text: 'Delete',
					handler: () => {
						this.store.dispatch(sheetTemplateManagementActions.deleteTemplateRequested({ templateId: template.id }));
					}
				}
			],
			backdropDismiss: false
		});
		alert.present();
	}
}
